import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
	callDeleteWebMenu,
	callGetPermissionControls,
	callGetSubMenu,
	callSaveWebMenu,
} from "../../../api/settings/service";
import CopyrightFooter from "../../../components/footer/employers/CopyrightFooter";
import DashboardHeader from "../../../components/header/DashboardHeader";
import DashboardEmployerSidebar from "../../../components/header/DashboardSidebar";
import MobileMenu from "../../../components/header/MobileMenu";
import { handleGetCookie } from "../../../utils/commonUtil";
import colors from "../../../utils/colors";
import "../../../components/i18n/lang";
import "../../style.css";
import "./web-menu.css";

const permissionLabelKeys = {
	0: "permission.actions.claim",
	1: "permission.actions.create",
	2: "permission.actions.edit",
	4: "permission.actions.delete",
	5: "permission.actions.approvePay",
	6: "permission.actions.rejectCancel",
	7: "permission.actions.print",
	8: "permission.actions.paymentForFee",
};

const buildMenuGroups = (items = []) => {
	const parents = items
		.filter((item) => item.mainMenuId === null || item.mainMenuId === undefined)
		.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));

	return parents.map((parent) => {
		const children = items.filter((item) => Number(item.mainMenuId) === Number(parent.id));
		const actionItems = children.filter(
			(item) => item.permisionControlId !== null && item.permisionControlId !== undefined
		);
		const usedActionIds = new Set();

		const pages = children
			.filter((item) => item.permisionControlId === null || item.permisionControlId === undefined)
			.map((page) => {
				const actions = actionItems
					.filter((action) =>
						action.name === page.name && (action.routeUrl || "") === (page.routeUrl || "")
					)
					.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
				actions.forEach((action) => usedActionIds.add(action.id));
				return { ...page, actions };
			})
			.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));

		const directActions = actionItems
			.filter((action) => !usedActionIds.has(action.id))
			.filter((action) =>
				action.name === parent.name && (action.routeUrl || "") === (parent.routeUrl || "")
			)
			.sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));

		return { ...parent, pages, actions: directActions };
	});
};

const emptyForm = (mode, context = {}) => {
	if (mode === "main") {
		return {
			id: null,
			name: "",
			groupName: "",
			groupIcons: "la-folder",
			description: "",
			routeUrl: "",
			priority: 999,
			actions: [],
			deletedActionIds: [],
		};
	}

	if (mode === "page") {
		return {
			id: null,
			mainMenuId: context.group?.id || "",
			name: "",
			description: "",
			routeUrl: "",
			icons: "",
			priority: 999,
			actions: [],
			deletedActionIds: [],
		};
	}

	return {
		id: null,
		targetId: context.target?.id || "",
		targetType: context.targetType || "page",
		permissionControlId: "",
		description: "",
		priority: 999,
	};
};

const WebMenuManager = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [items, setItems] = useState([]);
	const [permissionControls, setPermissionControls] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [editor, setEditor] = useState(null);
	const [form, setForm] = useState(null);
	const [loading, setLoading] = useState(false);

	const groups = useMemo(() => buildMenuGroups(items), [items]);

	const filteredGroups = useMemo(() => {
		const keyword = searchText.trim().toLowerCase();
		if (!keyword) return groups;

		return groups
			.map((group) => {
				const groupMatch = [group.name, group.groupName, group.description, group.routeUrl]
					.some((value) => String(value || "").toLowerCase().includes(keyword));

				const pages = group.pages.filter((page) => {
					const pageMatch = [page.name, page.description, page.routeUrl]
						.some((value) => String(value || "").toLowerCase().includes(keyword));
					const actionMatch = page.actions.some((action) =>
						[action.description, action.name, action.routeUrl]
							.some((value) => String(value || "").toLowerCase().includes(keyword))
					);
					return pageMatch || actionMatch;
				});
				const directActionMatch = (group.actions || []).some((action) =>
					[action.description, action.name, action.routeUrl]
						.some((value) => String(value || "").toLowerCase().includes(keyword))
				);

				return (groupMatch || directActionMatch) ? group : { ...group, pages };
			})
			.filter((group) => {
				const groupMatch = [group.name, group.groupName, group.description, group.routeUrl]
					.some((value) => String(value || "").toLowerCase().includes(keyword));
				const directActionMatch = (group.actions || []).some((action) =>
					[action.description, action.name, action.routeUrl]
						.some((value) => String(value || "").toLowerCase().includes(keyword))
				);
				return groupMatch || directActionMatch || group.pages.length > 0;
			});
	}, [groups, searchText]);

	const totalPages = useMemo(
		() => groups.reduce((total, group) => total + group.pages.length, 0),
		[groups]
	);

	const totalButtons = useMemo(
		() => groups.reduce(
			(total, group) => total + (group.actions?.length || 0) + group.pages.reduce((sum, page) => sum + page.actions.length, 0),
			0
		),
		[groups]
	);

	useEffect(() => {
		loadData();
	}, []);

	const loadData = async () => {
		setLoading(true);
		try {
			const [menuRsp, permissionRsp] = await Promise.all([
				dispatch(callGetSubMenu({ menuId: null, page: 1, maxResult: 5000 })),
				dispatch(callGetPermissionControls({})),
			]);

			const menuData = menuRsp?.payload?.responseCode === "00000" && Array.isArray(menuRsp.payload.data)
				? menuRsp.payload.data
				: [];
			if (menuRsp?.payload?.responseCode === "00000") {
				setItems(menuData);
			}

			if (permissionRsp?.payload?.responseCode === "00000") {
				setPermissionControls(Array.isArray(permissionRsp.payload.data) ? permissionRsp.payload.data : []);
			}
			return menuData;
		} catch (error) {
			console.error(error);
			toast.error(t("settings.webMenu.loadFailed"), { position: "top-left" });
		} finally {
			setLoading(false);
		}
		return [];
	};

	const getPermissionLabel = (permissionOrId) => {
		const id = typeof permissionOrId === "object"
			? permissionOrId?.id
			: permissionOrId;
		const permission = typeof permissionOrId === "object"
			? permissionOrId
			: permissionControls.find((item) => Number(item.id) === Number(id));

		if (permissionLabelKeys[id]) return t(permissionLabelKeys[id]);
		return permission?.name || `${t("settings.webMenu.permission")} #${id}`;
	};

	const selectMain = (group) => {
		setEditor({ mode: "main", group, item: group });
		setForm({
			id: group.id,
			name: group.name || "",
			groupName: group.groupName || "",
			groupIcons: group.groupIcons || "la-folder",
			description: group.description || "",
			routeUrl: group.routeUrl || "",
			priority: group.priority ?? 999,
			actions: (group.actions || []).map((action) => ({ ...action })),
			deletedActionIds: [],
		});
	};

	const selectPage = (group, page) => {
		setEditor({ mode: "page", group, page, item: page });
		setForm({
			id: page.id,
			mainMenuId: page.mainMenuId || group.id,
			name: page.name || "",
			description: page.description || "",
			routeUrl: page.routeUrl || "",
			icons: page.icons || "",
			priority: page.priority ?? 999,
			actions: (page.actions || []).map((action) => ({ ...action })),
			deletedActionIds: [],
		});
	};

	const openActionEditor = ({ parentEditor, parentForm, action = null, actionIndex = -1 }) => {
		const targetType = parentEditor.mode;
		const target = {
			...(targetType === "main" ? (parentEditor.group || {}) : (parentEditor.page || {})),
			...parentForm,
			id: parentForm.id || null,
		};

		setEditor({
			mode: "action",
			isNew: !action,
			group: parentEditor.group || (targetType === "main" ? target : null),
			page: targetType === "page" ? target : null,
			target,
			targetType,
			action,
			actionIndex,
			returnEditor: parentEditor,
			returnForm: {
				...parentForm,
				actions: (parentForm.actions || []).map((item) => ({ ...item })),
				deletedActionIds: [...(parentForm.deletedActionIds || [])],
			},
		});
		setForm(action ? {
			id: action.id || null,
			_draftId: action._draftId || null,
			targetId: target.id || "",
			targetType,
			permissionControlId: action.permisionControlId ?? action.permissionControlId ?? "",
			description: action.description || "",
			priority: action.priority ?? 999,
		} : {
			...emptyForm("action", { target, targetType }),
			_draftId: `draft_${Date.now()}`,
		});
	};

	const startNewMain = () => {
		setEditor({ mode: "main", isNew: true });
		setForm(emptyForm("main"));
	};

	const startNewPage = (group) => {
		setEditor({ mode: "page", isNew: true, group });
		setForm(emptyForm("page", { group }));
	};

	const startNewAction = (group, target, targetType = "page") => {
		const parentEditor = targetType === "main"
			? { mode: "main", group, item: group }
			: { mode: "page", group, page: target, item: target };
		const parentForm = targetType === "main" ? {
			id: group.id,
			name: group.name || "",
			groupName: group.groupName || "",
			groupIcons: group.groupIcons || "la-folder",
			description: group.description || "",
			routeUrl: group.routeUrl || "",
			priority: group.priority ?? 999,
			actions: (group.actions || []).map((item) => ({ ...item })),
			deletedActionIds: [],
		} : {
			id: target.id,
			mainMenuId: target.mainMenuId || group.id,
			name: target.name || "",
			description: target.description || "",
			routeUrl: target.routeUrl || "",
			icons: target.icons || "",
			priority: target.priority ?? 999,
			actions: (target.actions || []).map((item) => ({ ...item })),
			deletedActionIds: [],
		};
		openActionEditor({ parentEditor, parentForm });
	};

	const onChange = (e) => {
		const { name, value } = e.target;
		setForm((current) => ({ ...current, [name]: value }));
	};

	const validateForm = () => {
		if (!editor || !form) return false;
		if (editor.mode === "main" && !String(form.name || "").trim()) {
			toast.error(t("settings.webMenu.nameRequired"), { position: "top-left" });
			return false;
		}
		if (editor.mode === "main" && String(form.routeUrl || "").trim() && !String(form.routeUrl).startsWith("/")) {
			toast.error(t("settings.webMenu.routeMustStartSlash"), { position: "top-left" });
			return false;
		}
		if (editor.mode === "page") {
			if (!form.mainMenuId || !String(form.name || "").trim() || !String(form.routeUrl || "").trim()) {
				toast.error(t("settings.webMenu.pageRequired"), { position: "top-left" });
				return false;
			}
			if (!String(form.routeUrl).startsWith("/")) {
				toast.error(t("settings.webMenu.routeMustStartSlash"), { position: "top-left" });
				return false;
			}
		}
		if (editor.mode === "action" && (form.permissionControlId === "" || form.permissionControlId === null || form.permissionControlId === undefined)) {
			toast.error(t("settings.webMenu.permissionRequired"), { position: "top-left" });
			return false;
		}
		return true;
	};

	const handleAddButtonToCurrent = () => {
		if (!editor || !form || !["main", "page"].includes(editor.mode)) return;
		if (!validateForm()) return;
		if (editor.mode === "main" && !String(form.routeUrl || "").trim()) {
			toast.error(t("settings.webMenu.mainRouteRequiredForButton"), { position: "top-left" });
			return;
		}
		openActionEditor({ parentEditor: editor, parentForm: form });
	};

	const handleEditDraftAction = (action, actionIndex) => {
		if (!editor || !form || !["main", "page"].includes(editor.mode)) return;
		openActionEditor({ parentEditor: editor, parentForm: form, action, actionIndex });
	};

	const handleSaveActionDraft = () => {
		if (!editor || editor.mode !== "action" || !editor.returnEditor || !editor.returnForm) return;
		if (!validateForm()) return;

		const permissionId = Number(form.permissionControlId);
		const nextAction = {
			id: form.id || null,
			_draftId: form._draftId || `draft_${Date.now()}`,
			permisionControlId: permissionId,
			permissionControlId: permissionId,
			description: form.description || "",
			priority: Number.isFinite(Number(form.priority)) ? Number(form.priority) : 999,
		};

		const nextParentForm = {
			...editor.returnForm,
			actions: (editor.returnForm.actions || []).map((item) => ({ ...item })),
			deletedActionIds: [...(editor.returnForm.deletedActionIds || [])],
		};

		const duplicate = nextParentForm.actions.some((item, index) =>
			index !== editor.actionIndex &&
			Number(item.permisionControlId ?? item.permissionControlId) === permissionId
		);
		if (duplicate) {
			toast.error(t("settings.webMenu.duplicatePermission"), { position: "top-left" });
			return;
		}

		if (editor.actionIndex >= 0) {
			nextParentForm.actions[editor.actionIndex] = nextAction;
		} else {
			nextParentForm.actions.push(nextAction);
		}

		setEditor(editor.returnEditor);
		setForm(nextParentForm);
	};

	const handleBackFromAction = () => {
		if (!editor || editor.mode !== "action" || !editor.returnEditor || !editor.returnForm) return;
		setEditor(editor.returnEditor);
		setForm(editor.returnForm);
	};

	const handleSave = async (e) => {
		e.preventDefault();
		if (!validateForm()) return;

		if (editor.mode === "action") {
			handleSaveActionDraft();
			return;
		}

		const authData = handleGetCookie("auth");
		const carriredCode = authData?.user?.userId || "";
		const carriredName = `${authData?.user?.firstName || ""} ${authData?.user?.lastName || ""}`.trim();
		const payload = {
			...form,
			type: editor.mode,
			carriredCode,
			carriredName,
		};

		const result = await Swal.fire({
			title: t("titleSave"),
			text: t("confirmSave"),
			icon: "question",
			reverseButtons: true,
			showCancelButton: true,
			confirmButtonColor: colors.confirmMessage,
			cancelButtonColor: colors.cancel,
			confirmButtonText: t("confirm"),
			cancelButtonText: t("cancel"),
		});

		if (!result.isConfirmed) return;

		try {
			const rsp = await dispatch(callSaveWebMenu(payload));
			if (rsp?.payload?.responseCode === "00000") {
				await Swal.fire({
					icon: "success",
					title: t("success.add"),
					confirmButtonColor: colors.confirmMessage,
					confirmButtonText: t("close"),
				});
				const savedId = form.id || rsp?.payload?.data?.id;
				const freshItems = await loadData();
				const freshGroups = buildMenuGroups(Array.isArray(freshItems) ? freshItems : []);
				if (editor.mode === "main") {
					const freshGroup = freshGroups.find((item) => Number(item.id) === Number(savedId));
					if (freshGroup) selectMain(freshGroup);
				} else {
					for (const freshGroup of freshGroups) {
						const freshPage = freshGroup.pages.find((item) => Number(item.id) === Number(savedId));
						if (freshPage) {
							selectPage(freshGroup, freshPage);
							break;
						}
					}
				}
			} else if (rsp?.payload) {
				toast.error(`${rsp.payload.responseCode} - ${rsp.payload.responseDescription}`, { position: "top-left" });
			}
		} catch (error) {
			console.error(error);
			toast.error(t("settings.webMenu.saveFailed"), { position: "top-left" });
		}
	};

	// Deleting an action from the list only modifies the parent draft.
	const handleDeleteDraftAction = async (action) => {
		if (!editor || !form || !["main", "page"].includes(editor.mode)) return;
		const confirmation = await Swal.fire({
			title: t("titleDelete"),
			text: t("settings.webMenu.deleteActionDraftConfirm"),
			icon: "warning", reverseButtons: true, showCancelButton: true,
			confirmButtonColor: colors.confirmMessage,
			cancelButtonColor: colors.cancel,
			confirmButtonText: t("confirm"), cancelButtonText: t("cancel"),
		});
		if (!confirmation.isConfirmed) return;
		setForm((current) => {
			if (!current) return current;
			const index = (current.actions || []).findIndex((item) =>
				action.id ? Number(item.id) === Number(action.id) : item._draftId === action._draftId
			);
			if (index < 0) return current;
			const actions = [...current.actions];
			const [removed] = actions.splice(index, 1);
			return {
				...current,
				actions,
				deletedActionIds: removed?.id
					? [...new Set([...(current.deletedActionIds || []), removed.id])]
					: [...(current.deletedActionIds || [])],
			};
		});
	};

	// Menu/page delete buttons in the left tree use the existing server-side soft-delete.
	const handleDeleteTreeItem = async (item, mode) => {
		if (!item?.id || !["main", "page"].includes(mode)) return;
		const confirmation = await Swal.fire({
			title: t("titleDelete"),
			text: t(mode === "main" ? "settings.webMenu.deleteMainConfirm" : "settings.webMenu.deletePageConfirm"),
			icon: "warning", reverseButtons: true, showCancelButton: true,
			confirmButtonColor: colors.confirmMessage,
			cancelButtonColor: colors.cancel,
			confirmButtonText: t("confirm"), cancelButtonText: t("cancel"),
		});
		if (!confirmation.isConfirmed) return;
		try {
			const rsp = await dispatch(callDeleteWebMenu({ id: item.id }));
			if (rsp?.payload?.responseCode !== "00000") {
				toast.error(rsp?.payload?.responseDescription || t("settings.webMenu.deleteFailed"), { position: "top-left" });
				return;
			}
			const editedId = Number(editor?.mode === "action" ? editor?.target?.id : form?.id);
			if (editedId === Number(item.id) || (mode === "main" && Number(editor?.group?.id) === Number(item.id))) {
				setEditor(null);
				setForm(null);
			}
			await loadData();
			toast.success(t("success.delete"), { position: "top-left" });
		} catch (error) {
			console.error(error);
			toast.error(t("settings.webMenu.deleteFailed"), { position: "top-left" });
		}
	};

	const handleDelete = async () => {

		if (!form?.id) return;
		const typeLabel = t(`settings.webMenu.types.${editor.mode}`);
		const result = await Swal.fire({
			title: t("titleDelete"),
			text: `${t("settings.webMenu.deleteConfirm")} ${typeLabel}?`,
			icon: "warning",
			reverseButtons: true,
			showCancelButton: true,
			confirmButtonColor: colors.confirmMessage,
			cancelButtonColor: colors.cancel,
			confirmButtonText: t("confirm"),
			cancelButtonText: t("cancel"),
		});

		if (!result.isConfirmed) return;

		try {
			const rsp = await dispatch(callDeleteWebMenu({ id: form.id }));
			if (rsp?.payload?.responseCode === "00000") {
				setEditor(null);
				setForm(null);
				await loadData();
				Swal.fire({
					icon: "success",
					title: t("success.delete"),
					confirmButtonColor: colors.confirmMessage,
					confirmButtonText: t("close"),
				});
			}
		} catch (error) {
			console.error(error);
			toast.error(t("settings.webMenu.deleteFailed"), { position: "top-left" });
		}
	};

	const renderEditorTitle = () => {
		if (!editor) return "";
		const prefix = editor.isNew ? t("button.add") : t("button.edit");
		return `${prefix} ${t(`settings.webMenu.types.${editor.mode}`)}`;
	};

	const activeId = form?.id;

	return (
		<div className="page-wrapper dashboard">
			<span className="header-span" />
			<DashboardHeader />
			<MobileMenu />
			<DashboardEmployerSidebar />

			<section className="user-dashboard">
				<div className="dashboard-outer">
					<div className="upper-title-box web-menu-title-row">
						<div>
							<h4>{t("settings.webMenu.title")}</h4>
							<p className="web-menu-subtitle">{t("settings.webMenu.subtitle")}</p>
						</div>
						<div className="group-button-header d-flex gap-2">
							<button
								type="button"
								className="btn btn-outline-secondary"
								onClick={() => navigate("/admin/setting/userPermission")}
							>
								<i className="fas fa-arrow-left" /> {t("claimPages.common.back")}
							</button>
							<button type="button" className="btn btn-primary" onClick={startNewMain}>
								<i className="fas fa-plus" /> {t("settings.webMenu.addMain")}
							</button>
						</div>
					</div>

					<div className="web-menu-stats">
						<div><strong>{groups.length}</strong><span>{t("settings.webMenu.mainMenus")}</span></div>
						<div><strong>{totalPages}</strong><span>{t("settings.webMenu.pages")}</span></div>
						<div><strong>{totalButtons}</strong><span>{t("settings.webMenu.buttons")}</span></div>
					</div>

					<div className="ls-widget">
						<div className="tabs-box">
							<div className="widget-content p-4">
								<div className="permission-toolbar web-menu-toolbar">
									<div className="permission-search">
										<i className="la la-search" />
										<input
											type="text"
											placeholder={t("settings.webMenu.searchPlaceholder")}
											value={searchText}
											onChange={(e) => setSearchText(e.target.value)}
										/>
									</div>
									<div className="web-menu-refresh-note">
										<i className="la la-info-circle" /> {t("settings.webMenu.permissionHint")}
									</div>
								</div>

								<div className="permission-simulator web-menu-builder">
									<aside className="permission-site-map">
										<div className="permission-site-map-head">
											<span className="permission-logo-mark"><i className="la la-sitemap" /></span>
											<div>
												<strong>{t("settings.webMenu.treeTitle")}</strong>
												<small>{t("settings.webMenu.treeHint")}</small>
											</div>
										</div>

										<div className="permission-site-groups web-menu-tree">
											{loading ? (
												<div className="web-menu-loading"><i className="la la-spinner la-spin" /> {t("settings.webMenu.loading")}</div>
											) : filteredGroups.length > 0 ? filteredGroups.map((group) => (
												<section className="permission-site-group" key={group.id}>
													<div className={`permission-site-group-title web-menu-group-title ${editor?.mode === "main" && activeId === group.id ? "active" : ""}`}>
														<button type="button" className="web-menu-tree-main" onClick={() => selectMain(group)}>
															<span className="permission-group-icon"><i className={`la ${group.groupIcons || "la-folder"}`} /></span>
															<span>
																<strong>{t(group.name || group.groupName)}</strong>
																<small>{group.pages.length} {t("settings.webMenu.pages")} · {group.actions?.length || 0} {t("settings.webMenu.buttons")}</small>
															</span>
														</button>
														{group.routeUrl && (
													<button type="button" className="web-menu-icon-button" title={t("settings.webMenu.addButton")} onClick={() => (editor?.mode === "main" && Number(form?.id) === Number(group.id)) ? handleAddButtonToCurrent() : startNewAction(group, group, "main")}>
														<i className="la la-mouse-pointer" />
													</button>
												)}
												<button type="button" className="web-menu-icon-button" title={t("settings.webMenu.addPage")} onClick={() => startNewPage(group)}>
															<i className="la la-plus" />
														</button>
															<button type="button" className="web-menu-icon-button web-menu-delete-button" title={t("button.delete")} aria-label={`${t("button.delete")} ${t(group.name || group.groupName)}`} onClick={() => handleDeleteTreeItem(group, "main")}>
																<i className="la la-trash" />
															</button>
													</div>

													<div className="permission-site-pages">
														{group.pages.map((page) => (
															<div className={`web-menu-page-row ${editor?.page?.id === page.id || (editor?.mode === "page" && activeId === page.id) ? "active" : ""}`} key={page.id}>
																<button type="button" className="web-menu-page-button" onClick={() => selectPage(group, page)}>
																	<i className={`la ${page.icons || "la-file-alt"}`} />
																	<span>
																		<strong>{t(page.name)}</strong>
																		<small>{page.routeUrl || "-"}</small>
																	</span>
																	<em>{page.actions.length}</em>
																</button>
																<button type="button" className="web-menu-icon-button" title={t("settings.webMenu.addButton")} onClick={() => (editor?.mode === "page" && Number(form?.id) === Number(page.id)) ? handleAddButtonToCurrent() : startNewAction(group, page)}>
																	<i className="la la-plus" />
																</button>
															<button type="button" className="web-menu-icon-button web-menu-delete-button" title={t("button.delete")} aria-label={`${t("button.delete")} ${t(page.name || page.groupName)}`} onClick={() => handleDeleteTreeItem(page, "page")}>
																<i className="la la-trash" />
															</button>
															</div>
														))}
														{group.pages.length === 0 && (
															<button type="button" className="web-menu-empty-page" onClick={() => startNewPage(group)}>
																<i className="la la-plus-circle" /> {t("settings.webMenu.addFirstPage")}
															</button>
														)}
													</div>
												</section>
											)) : (
												<div className="web-menu-loading">{t("nodata")}</div>
											)}
										</div>
									</aside>

									<main className="permission-browser web-menu-editor">
										<div className="permission-browser-bar">
											<div className="permission-browser-dots"><i /><i /><i /></div>
											<div className="permission-browser-address">
												<i className="la la-lock" />
												<span>{editor?.target?.routeUrl || editor?.page?.routeUrl || form?.routeUrl || "/admin/setting/webMenu"}</span>
											</div>
										</div>

										{editor && form ? (
											<form className="web-menu-form" onSubmit={handleSave}>
												<div className="web-menu-editor-head">
													<div>
														<span className="permission-preview-eyebrow">{t(`settings.webMenu.types.${editor.mode}`)}</span>
														<h4>{renderEditorTitle()}</h4>
														<p>{t(`settings.webMenu.help.${editor.mode}`)}</p>
													</div>
													{editor.mode !== "action" && editor.item?.id && (
															<span className="web-menu-id-badge">ID #{editor.item.id}</span>
														)}
												</div>

												<div className="web-menu-form-grid">
													{editor.mode === "main" && (
														<>
															<div className="form-group">
																<label>{t("settings.webMenu.menuName")} <span className="text-danger">*</span></label>
																<input className="form-control-uld" name="name" value={form.name} onChange={onChange} placeholder="menu.setting.name" required />
															</div>
															<div className="form-group">
																<label>{t("settings.webMenu.groupName")}</label>
																<input className="form-control-uld" name="groupName" value={form.groupName} onChange={onChange} placeholder={t("settings.webMenu.groupNamePlaceholder")} />
															</div>
															<div className="form-group">
																<label>{t("settings.webMenu.icon")}</label>
																<div className="web-menu-icon-input">
																	<span><i className={`la ${form.groupIcons || "la-folder"}`} /></span>
																	<input className="form-control-uld" name="groupIcons" value={form.groupIcons} onChange={onChange} placeholder="la-tools" />
																</div>
															</div>
															<div className="form-group">
																<label>{t("settings.webMenu.priority")}</label>
																<input type="number" className="form-control-uld" name="priority" value={form.priority} onChange={onChange} />
															</div>
															<div className="form-group web-menu-span-2">
																<label>{t("settings.webMenu.mainRoute")}</label>
																<input className="form-control-uld" name="routeUrl" value={form.routeUrl} onChange={onChange} placeholder="/admin/..." />
															</div>
														</>
													)}

													{editor.mode === "page" && (
														<>
															<div className="form-group">
																<label>{t("settings.webMenu.mainMenu")} <span className="text-danger">*</span></label>
																<select className="form-control-uld" name="mainMenuId" value={form.mainMenuId} onChange={onChange} required>
																	<option value="">{t("settings.webMenu.selectMain")}</option>
																	{groups.map((group) => <option value={group.id} key={group.id}>{t(group.name || group.groupName)}</option>)}
																</select>
															</div>
															<div className="form-group">
																<label>{t("settings.webMenu.pageName")} <span className="text-danger">*</span></label>
																<input className="form-control-uld" name="name" value={form.name} onChange={onChange} placeholder="menu.setting.users" required />
															</div>
															<div className="form-group web-menu-span-2">
																<label>{t("settings.webMenu.route")} <span className="text-danger">*</span></label>
																<input className="form-control-uld" name="routeUrl" value={form.routeUrl} onChange={onChange} placeholder="/admin/setting/users" required />
															</div>
															<div className="form-group">
																<label>{t("settings.webMenu.icon")}</label>
																<div className="web-menu-icon-input">
																	<span><i className={`la ${form.icons || "la-file-alt"}`} /></span>
																	<input className="form-control-uld" name="icons" value={form.icons} onChange={onChange} placeholder="la-user" />
																</div>
															</div>
															<div className="form-group">
																<label>{t("settings.webMenu.priority")}</label>
																<input type="number" className="form-control-uld" name="priority" value={form.priority} onChange={onChange} />
															</div>
														</>
													)}

													{editor.mode === "action" && (
														<>
															<div className="form-group web-menu-span-2">
																<label>{editor.targetType === "main" ? t("settings.webMenu.mainMenu") : t("settings.webMenu.page")}</label>
																<div className="web-menu-readonly-card">
																	<i className={editor.targetType === "main" ? "la la-folder-open" : "la la-file-alt"} />
																	<span><strong>{t(editor.target?.name)}</strong><small>{editor.target?.routeUrl}</small></span>
																</div>
															</div>
															<div className="form-group">
																<label>{t("settings.webMenu.permission")} <span className="text-danger">*</span></label>
																<select className="form-control-uld" name="permissionControlId" value={form.permissionControlId} onChange={onChange} required>
																	<option value="">{t("settings.webMenu.selectPermission")}</option>
																	{permissionControls.map((permission) => (
																		<option value={permission.id} key={permission.id}>{getPermissionLabel(permission)} (ID {permission.id})</option>
																	))}
																</select>
															</div>
															<div className="form-group">
																<label>{t("settings.webMenu.priority")}</label>
																<input type="number" className="form-control-uld" name="priority" value={form.priority} onChange={onChange} />
															</div>
														</>
													)}

													<div className="form-group web-menu-span-2">
														<label>{editor.mode === "action" ? t("settings.webMenu.buttonLabel") : t("settings.webMenu.description")}</label>
														<textarea className="form-control-uld web-menu-textarea" name="description" value={form.description} onChange={onChange} placeholder={t("settings.webMenu.descriptionPlaceholder")} />
													</div>
												</div>

												{["main", "page"].includes(editor.mode) && (
													<section className="web-menu-actions-section">
														<div className="web-menu-actions-head">
															<div>
																<h6><i className="la la-mouse-pointer" /> {t("settings.webMenu.targetButtons")}</h6>
																<p>{t("settings.webMenu.targetButtonsHint")}</p>
															</div>
															<button type="button" className="btn btn-sm btn-outline-primary" onClick={handleAddButtonToCurrent}>
																<i className="la la-plus" /> {t("settings.webMenu.addButton")}
															</button>
														</div>
														<div className="web-menu-action-list">
												{(form.actions || []).length > 0 ?
													(form.actions || []).map((action, actionIndex) => {
														return (
															<div className="web-menu-action-card" key={action.id || action._draftId || actionIndex}>
																				<button type="button" className="web-menu-action-edit" onClick={() => handleEditDraftAction(action, actionIndex)}>
																			<span className="web-menu-action-icon"><i className="la la-mouse-pointer" /></span>
																			<span><strong>{action.description || getPermissionLabel(action.permisionControlId)}</strong><small>{getPermissionLabel(action.permisionControlId)} · ID {action.permisionControlId}</small></span>
																			<i className="la la-pen" />
																		</button>
																				<button type="button" className="web-menu-icon-button web-menu-delete-button" title={t("button.delete")} aria-label={`${t("button.delete")} ${action.description || getPermissionLabel(action.permisionControlId)}`} onClick={() => handleDeleteDraftAction(action)}>
																					<i className="la la-trash" />
																				</button>
																			</div>
														);
													}) : (
													<div className="web-menu-no-actions"><i className="la la-info-circle" /> {t("settings.webMenu.noButtons")}</div>
												)}
											</div>
													</section>
												)}

												<div className="web-menu-form-actions">
												{editor.mode === "action" ? (
													<>
														<button type="button" className="btn btn-outline-secondary" onClick={handleBackFromAction}>
															<i className="fas fa-arrow-left" /> {t("claimPages.common.back")}
														</button>
														<button type="submit" className="theme-btn btn-style-one">
															<i className="la la-save" /> {t("button.save")}
														</button>
													</>
											) : (
													<>
														{form.id && (
															<button type="button" className="btn btn-outline-danger" onClick={handleDelete}>
																<i className="la la-trash" /> {t("button.delete")}
															</button>
														)}
														<button type="submit" className="theme-btn btn-style-one">
															<i className="la la-save" /> {t("button.save")}
														</button>
													</>
												)}
											</div>
											</form>
										) : (
											<div className="web-menu-welcome">
												<span><i className="la la-sitemap" /></span>
												<h4>{t("settings.webMenu.selectTitle")}</h4>
												<p>{t("settings.webMenu.selectHint")}</p>
												<button type="button" className="theme-btn btn-style-one" onClick={startNewMain}>
													<i className="la la-plus" /> {t("settings.webMenu.addMain")}
												</button>
											</div>
										)}
									</main>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
			<CopyrightFooter />
		</div>
	);
};

export default WebMenuManager;
